import React, { useRef, useState } from 'react';
import { ArrowRight, Volume2, VolumeX } from 'lucide-react';

interface HeroSectionProps {
  onBuildClick: () => void;
  onWholesaleClick: () => void;
  onLoginClick?: () => void;
}

const HeroSection: React.FC<HeroSectionProps> = ({ onBuildClick, onWholesaleClick, onLoginClick }) => {
  // Hero theme music — off until the visitor taps the speaker (browsers block
  // unmuted autoplay anyway), and preload="none" so the track costs zero
  // bandwidth for everyone who never turns it on.
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const toggleMusic = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      audio.volume = 0.3;
      audio.play().then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
    }
  };

  return (
    <section className="relative w-full h-[78vh] min-h-[640px] overflow-hidden bg-forge-deep">

      <audio ref={audioRef} src="/audio/hero-music.mp3" loop preload="none" />

      {/* Background image with refined editorial overlay */}
      <div className="absolute inset-0">
        <img
          src="/images/hero.webp"
          alt=""
          className="w-full h-full object-cover opacity-70"
          style={{ objectPosition: 'center' }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(95deg, #0E1518 0%, rgba(14, 21, 24, 0.85) 35%, rgba(14, 21, 24, 0.3) 65%, transparent 100%)',
          }}
        />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-editorial mx-auto px-6 lg:px-10 h-full flex flex-col justify-center">

        {/* Eyebrow */}
        <div className="animate-in fade-in fill-mode-backwards duration-700 delay-100 mb-8">
          <span className="font-mono text-[11px] tracking-micro uppercase text-ember-soft">
            Drop-forged in Colombia &middot; Since 1960
          </span>
        </div>

        {/* Headline */}
        <h1 className="font-editorial italic font-light text-paper-warm tracking-[-0.02em] leading-[1.02] mb-8 text-[clamp(3rem,7vw,5.5rem)] max-w-prose animate-in fade-in slide-in-from-bottom-2 fill-mode-backwards duration-1000 delay-300">
          Island soul.<br />
          <span className="text-paper/85">Northern soil.</span>
        </h1>

        {/* Lede */}
        <p className="max-w-prose text-lg lg:text-xl text-paper/75 leading-[1.55] mb-8 font-light animate-in fade-in fill-mode-backwards duration-1000 delay-500">
          The essential tool of everyday Caribbean life — drop-forged by Incolma in Colombia
          since 1960. Now yours to own — shipped direct across Canada for your garden,
          your trail, and your kitchen.
        </p>

        {/* Tagline stamp */}
        <p className="mb-12 font-mono text-xs lg:text-sm tracking-[0.22em] uppercase text-brass animate-in fade-in fill-mode-backwards duration-1000 delay-500">
          Blade sharp. Yard clean.
        </p>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row items-start gap-4 animate-in fade-in fill-mode-backwards duration-1000 delay-700">
          <button
            onClick={onBuildClick}
            className="group inline-flex items-center gap-3 bg-ember hover:bg-ember-deep text-paper-warm px-7 py-3.5 text-base font-medium transition-colors duration-150 ease-editorial"
          >
            <span>Shop the collection</span>
            <ArrowRight className="w-4 h-4 transition-transform duration-300 ease-editorial group-hover:translate-x-1" />
          </button>
          <button
            onClick={onWholesaleClick}
            className="inline-flex items-center gap-3 text-paper border border-paper/30 hover:border-paper hover:bg-paper/5 px-7 py-3.5 text-base font-medium transition-colors duration-150 ease-editorial"
          >
            <span>Wholesale &amp; trade</span>
          </button>
        </div>

        {/* Sign in link */}
        {onLoginClick && (
          <div className="mt-6 animate-in fade-in fill-mode-backwards duration-1000 delay-700">
            <button
              onClick={onLoginClick}
              className="text-sm text-paper/55 hover:text-paper/90 transition-colors duration-150"
            >
              Buying for a business?{' '}
              <span className="underline underline-offset-4 decoration-paper/40">Sign in</span>
            </button>
          </div>
        )}
      </div>

      {/* Music toggle — floats in the spot the WhatsApp button used to occupy,
          just above the chat bubble */}
      <button
        onClick={toggleMusic}
        className="fixed bottom-[5.25rem] right-6 z-50 inline-flex items-center gap-2.5 bg-forge-deep/80 backdrop-blur-sm border border-paper/20 hover:border-brass text-paper/80 hover:text-paper-warm px-4 py-2.5 rounded-full shadow-raise hover:shadow-float transition-all duration-200 ease-editorial"
        aria-label={isPlaying ? 'Pause theme music' : 'Play theme music'}
      >
        {isPlaying ? (
          <Volume2 className="w-4 h-4 text-brass" strokeWidth={1.5} />
        ) : (
          <VolumeX className="w-4 h-4" strokeWidth={1.5} />
        )}
        <span className="font-mono text-[10px] tracking-micro uppercase">
          {isPlaying ? 'Music on' : 'Play music'}
        </span>
      </button>

      {/* Bottom edge: thin brass rule */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brass/40 to-transparent" />
    </section>
  );
};

export default HeroSection;
